import React, { useState } from "react";
import styles from "./Assessment.module.css";
import { AnswerReveal, ReferenceText } from "./AnswerReveal";

const ProblemPane = ({ problem }) => {
  const [activeTab, setActiveTab] = useState("problem");

  if (!problem) return null;

  const testcases = problem.testcases_data || [];
  const visibleTestCases = testcases.filter(tc => !tc.is_hidden).length;
  const hiddenTestCases = testcases.filter(tc => tc.is_hidden).length;
  const maxScore = problem.max_score || testcases.reduce((sum, tc) => sum + (tc.score || 0), 0);

  // Parse description for objective and example
  const description = problem.description || "";

  // Simple parsing - assumes format like "Problem statement\n\nExample:\nInput: ...\nOutput: ..."
  const lines = description.split("\n");
  const objective = lines[0] || "Solve the problem as described.";

  // Try to extract example from test cases or description
  const exampleTestCase = testcases[0];
  const exampleInput = exampleTestCase?.input || "";
  const exampleOutput = exampleTestCase?.output || "";

  // Constraints (could be parsed from description or hardcoded)
  const constraints = [
    "Time limit: 2s",
    "Memory limit: 256 MB",
  ];

  return (
    <div className={styles.problemPane}>
      {/* Tabs */}
      <div className={styles.paneTabs}>
        <button
          className={`${styles.paneTab} ${activeTab === "problem" ? styles.paneTabActive : ""}`}
          onClick={() => setActiveTab("problem")}
        >
          Problem
        </button>
        <button
          className={`${styles.paneTab} ${activeTab === "examples" ? styles.paneTabActive : ""}`}
          onClick={() => setActiveTab("examples")}
        >
          Examples
        </button>
        <button
          className={`${styles.paneTab} ${activeTab === "hints" ? styles.paneTabActive : ""}`}
          onClick={() => setActiveTab("hints")}
        >
          Hints
        </button>
        {problem.answer_key && (
          <button
            className={`${styles.paneTab} ${activeTab === "solution" ? styles.paneTabActive : ""}`}
            onClick={() => setActiveTab("solution")}
          >
            Solution
          </button>
        )}
      </div>

      {/* Content */}
      <div className={styles.problemContent}>
        {activeTab === "problem" && (
          <>
            {/* Objective */}
            <div>
              <div className={styles.sectionLabel}>Objective</div>
              <p className={styles.objectiveText}>{description}</p>
            </div>

            {/* Example */}
            {exampleInput && (
              <div>
                <div className={styles.sectionLabel}>Example</div>
                <div className={styles.exampleBlock}>
                  Input:  {exampleInput}
                  {"\n"}Output: {exampleOutput}
                </div>
              </div>
            )}

            {/* Constraints */}
            <div>
              <div className={styles.sectionLabel}>Constraints</div>
              <div className={styles.constraintsList}>
                {constraints.map((constraint, i) => (
                  <div key={i} className={styles.constraint}>
                    <span className={styles.constraintBullet}>•</span>
                    <span>{constraint}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Test Case Info */}
            <div className={styles.testCaseInfo}>
              <div className={styles.testCaseRow}>
                <span>Visible test cases</span>
                <span className={styles.testCaseValue}>{visibleTestCases}</span>
              </div>
              <div className={styles.testCaseRow}>
                <span>Hidden test cases</span>
                <span className={styles.testCaseValue}>{hiddenTestCases}</span>
              </div>
              <div className={styles.testCaseRow}>
                <span>Max score</span>
                <span className={styles.testCaseValue}>{maxScore}</span>
              </div>
            </div>
          </>
        )}

        {activeTab === "examples" && (
          <div>
            <div className={styles.sectionLabel}>Test Case Examples</div>
            {testcases.filter(tc => !tc.is_hidden).slice(0, 3).map((tc, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                  Example {i + 1}
                </div>
                <div className={styles.exampleBlock}>
                  Input:{"\n"}{tc.input || "(empty)"}
                  {"\n\n"}Expected Output:{"\n"}{tc.output || "(empty)"}
                </div>
              </div>
            ))}
            {testcases.filter(tc => !tc.is_hidden).length === 0 && (
              <p className={styles.objectiveText}>No visible examples available.</p>
            )}
          </div>
        )}

        {activeTab === "solution" && problem.answer_key && (
          <AnswerReveal title="Reference solution">
            <ReferenceText text={problem.answer_key.solution} code />
          </AnswerReveal>
        )}

        {activeTab === "hints" && (
          <div>
            <div className={styles.sectionLabel}>Hints</div>
            <p className={styles.objectiveText}>
              Think about edge cases and optimize your solution for time complexity.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemPane;
